import { MedicineDispensabilityStatus, PharmacyPartnershipType, PharmacySortType } from '@/types/bff/gql-schema';
import {
  DispensationUnitVo,
  ENearbyPharmacySearchFilterType,
  ENearbyPharmacySearchSortType,
  EPharmacyFilterMode,
  EPharmacyOperationType,
  EPrescriptionSendType,
  NearbyPharmacyMedicineVo,
  PharmacySearchCommonFilterMap,
} from '@/types/pharmacy';
import { formatKilometerByMeter, formatPriceComma } from '@/utils/format';
import { isWebView } from '@/utils/mobile/RNPlugins';

/** 약국 정렬 필터 */
const PHARMACY_SEARCH_SORT_TYPE_NAME_MAP: {
  [key in ENearbyPharmacySearchSortType]: string;
} = {
  [ENearbyPharmacySearchSortType.VisitCount]: '방문많은순',
  [ENearbyPharmacySearchSortType.Distance]: '가까운순',
  [ENearbyPharmacySearchSortType.QuickReceipt]: '추천순',
  [ENearbyPharmacySearchSortType.LowPrice]: '가격낮은순',
  [ENearbyPharmacySearchSortType.MostReviews]: '리뷰많은순',
  [ENearbyPharmacySearchSortType.HighRating]: '평점높은순',
};
const PHARMACY_SEARCH_SORT_TYPE_DTO_MAP: {
  [key in ENearbyPharmacySearchSortType]: PharmacySortType;
} = {
  [ENearbyPharmacySearchSortType.VisitCount]: PharmacySortType.VisitCount,
  [ENearbyPharmacySearchSortType.Distance]: PharmacySortType.Distance,
  [ENearbyPharmacySearchSortType.QuickReceipt]: PharmacySortType.QuickReceipt,
  [ENearbyPharmacySearchSortType.LowPrice]: PharmacySortType.LowPrice,
  [ENearbyPharmacySearchSortType.MostReviews]: PharmacySortType.MostReviews,
  [ENearbyPharmacySearchSortType.HighRating]: PharmacySortType.HighRating,
};

export enum EPharmacyMainTagType {
  HighDispensationProbability = 'HIGH_DISPENSATION_PROBABILITY',
  ConfirmedDispensation = 'CONFIRMED_DISPENSATION',
  PossibleDispensation = 'POSSIBLE_DISPENSATION',
  Unknown = 'UNKNOWN',
}

export enum EPharmacyTagType {
  RequestWithApp = 'REQUEST_WITH_APP',
  RequestWithCall = 'REQUEST_WITH_CALL',
  OpenAtLateNight = 'OPEN_AT_LATE_NIGHT',
  OpenEveryday = 'OPEN_EVERYDAY',
  PediatricsMedicine = 'PEDIATRICS_MEDICINE',
  PsychiatryMedicine = 'PSYCHIATRY_MEDICINE',
  ReservationWithApp = 'RESERVATION_WITH_APP',
  ImmediatePayment = 'IMMEDIATE_PAYMENT',
  PrescriptionReject = 'PRESCRIPTION_REJECT',
  Unknown = 'UNKNOWN',
}

export enum EPharmacyParkingType {
  Free = 'FREE',
  Paid = 'PAID',
  Unavailable = 'UNAVAILABLE',
}

export enum EPharmacyPriceTagType {
  verifiedPrice = 'VERIFIED_PRICE',
  reportedPrice = 'REPORTED_PRICE',
}

export class NearbyPharmacySortFilterModel {
  readonly type;
  constructor(type: ENearbyPharmacySearchSortType) {
    this.type = type;
  }

  getTitle() {
    return PHARMACY_SEARCH_SORT_TYPE_NAME_MAP[this.type];
  }
  get dtoType() {
    return PHARMACY_SEARCH_SORT_TYPE_DTO_MAP[this.type];
  }
  isSelected(selectedType?: ENearbyPharmacySearchSortType) {
    return this.type === selectedType;
  }
}

export class NearbyPharmacyFilterModel {
  constructor(
    private _pharmacyFilter: {
      activeIconUrl?: string;
      inactiveIconUrl?: string;
      description?: string;
      title: string;
      type: ENearbyPharmacySearchFilterType;
      mode: EPharmacyFilterMode;
      subFilters: NearbyPharmacyFilterModel[];
    }
  ) {}

  get title() {
    return this._pharmacyFilter.title;
  }

  getSelectedListFilter(selectedTypes: ENearbyPharmacySearchFilterType[]) {
    if (!this._pharmacyFilter.subFilters) return this;
    if (!selectedTypes.length) return this;

    const selectedSubFilter = this._pharmacyFilter.subFilters.filter((subFilter) =>
      selectedTypes.includes(subFilter.type)
    )[0];
    if (!selectedSubFilter) return this;

    return selectedSubFilter;
  }

  get mode() {
    return this._pharmacyFilter.mode;
  }

  get type() {
    return this._pharmacyFilter.type;
  }

  get activeIconUrl() {
    return this._pharmacyFilter.activeIconUrl;
  }

  get inactiveIconUrl() {
    return this._pharmacyFilter.inactiveIconUrl;
  }

  get description() {
    return this._pharmacyFilter.description;
  }

  get subFilters() {
    return this._pharmacyFilter.subFilters;
  }

  get subFilterTypes() {
    if (!this._pharmacyFilter.subFilters) return [];
    return this._pharmacyFilter.subFilters?.map((subFilter) => subFilter.type);
  }

  isSelectedFilter(selectedTypes: ENearbyPharmacySearchFilterType[]) {
    if (!selectedTypes) return false;
    return selectedTypes.includes(this.type);
  }

  isSelectedSubFilter(selectedTypes: ENearbyPharmacySearchFilterType[]) {
    return this._pharmacyFilter.subFilters?.some((subFilter) => selectedTypes.includes(subFilter.type));
  }
}

/**
 * 약국 일반필터
 */

const PHARMACY_COMMON_FILTER_NAME_MAP: {
  [key in ENearbyPharmacySearchFilterType]: string;
} = {
  [ENearbyPharmacySearchFilterType.Operating]: '영업중',
  [ENearbyPharmacySearchFilterType.HandlingMedicine]: '조제가능',
  [ENearbyPharmacySearchFilterType.CheapPrice]: '착한가격',
  [ENearbyPharmacySearchFilterType.IsOpenAtLateNight]: '심야',
  [ENearbyPharmacySearchFilterType.IsOpenEveryDay]: '연중무휴',
  [ENearbyPharmacySearchFilterType.IncludeDiagnosisPrice]: '진료비 포함',
  [ENearbyPharmacySearchFilterType.ImmediatePayment]: 'NOW',
  [ENearbyPharmacySearchFilterType.Now]: 'NOW',
  [ENearbyPharmacySearchFilterType.RequestWithApp]: '앱으로접수',
  [ENearbyPharmacySearchFilterType.DispensationProbability]: '조제가능성',
  [ENearbyPharmacySearchFilterType.SameMedicine]: '대체조제 제외',
  [ENearbyPharmacySearchFilterType.ConfirmedDispensation]: '조제확실',
  [ENearbyPharmacySearchFilterType.HighDispensationProbability]: '조제가능성높음',
  [ENearbyPharmacySearchFilterType.PossibleDispensation]: '조제가능성있음',
};

export class PharmacyPickupCommonFilterModel {
  constructor(public values: PharmacySearchCommonFilterMap) {}
  getTitle(key: keyof PharmacySearchCommonFilterMap): string {
    return PHARMACY_COMMON_FILTER_NAME_MAP[key];
  }
  isSelected(key: keyof PharmacySearchCommonFilterMap): boolean {
    return this.values[key];
  }
}

export class PharmacyTagModel {
  constructor(
    private _pharmacyTag: {
      type: EPharmacyTagType;
      imageUrl: string;
    }
  ) {}

  get type() {
    return this._pharmacyTag.type;
  }

  get imageUrl() {
    return this._pharmacyTag.imageUrl;
  }

  isOpenAtLateNight() {
    return this._pharmacyTag.type === EPharmacyTagType.OpenAtLateNight;
  }

  isOpenEveryDay() {
    return this._pharmacyTag.type === EPharmacyTagType.OpenEveryday;
  }

  isImmediatePayment() {
    return this._pharmacyTag.type === EPharmacyTagType.ImmediatePayment;
  }

  isSendPrescriptionRequestWithApp() {
    return this._pharmacyTag.type === EPharmacyTagType.RequestWithApp;
  }

  isSendPrescriptionReservationWithApp() {
    return this._pharmacyTag.type === EPharmacyTagType.ReservationWithApp;
  }

  isSendPrescriptionWithCall() {
    return this._pharmacyTag.type === EPharmacyTagType.RequestWithCall;
  }

  isClusteredPharmacyModalAllowedTag() {
    return (
      this._pharmacyTag.type === EPharmacyTagType.RequestWithApp ||
      this._pharmacyTag.type === EPharmacyTagType.RequestWithCall ||
      this._pharmacyTag.type === EPharmacyTagType.ReservationWithApp ||
      this._pharmacyTag.type === EPharmacyTagType.PrescriptionReject
    );
  }

  isPrescriptionReject() {
    return this._pharmacyTag.type === EPharmacyTagType.PrescriptionReject;
  }

  hasRequestWithAppTag() {
    return (
      this._pharmacyTag.type === EPharmacyTagType.RequestWithApp ||
      this._pharmacyTag.type === EPharmacyTagType.ReservationWithApp
    );
  }
}

export class PharmacyMainTagModel {
  constructor(
    private _pharmacyMainTag: {
      type: EPharmacyMainTagType;
      imageUrl: string;
    }
  ) {}

  get type() {
    return this._pharmacyMainTag.type;
  }

  get imageUrl() {
    return this._pharmacyMainTag.imageUrl;
  }

  isHighDispensationProbability() {
    return this._pharmacyMainTag.type === EPharmacyMainTagType.HighDispensationProbability;
  }

  isPossibleDispensation() {
    return this._pharmacyMainTag.type === EPharmacyMainTagType.PossibleDispensation;
  }

  isConfirmedDispensation() {
    return this._pharmacyMainTag.type === EPharmacyMainTagType.ConfirmedDispensation;
  }
}

export class PharmacyBadgeModel {
  constructor(
    private _pharmacyBadge: {
      type: string;
      imageUrl: string;
    }
  ) {}

  get type() {
    return this._pharmacyBadge.type;
  }

  get imageUrl() {
    return this._pharmacyBadge.imageUrl;
  }
}

export class PharmacyNextOperationHourModel {
  constructor(
    private _pharmacyNextOperationHour: {
      earliestAvailableReservationHour?: number | null;
      earliestAvailableReservationHourString?: string | null;
    }
  ) {}

  get earliestAvailableReservationHour() {
    return this._pharmacyNextOperationHour.earliestAvailableReservationHour;
  }

  get earliestAvailableReservationHourString() {
    return this._pharmacyNextOperationHour.earliestAvailableReservationHourString;
  }
}

export class PharmacyDispensableMedicineModel {
  constructor(
    private _pharmacyDispensableMedicine: {
      totalAmount: number | null;
      dispensableMedicines: DispensableMedicineModel[];
    }
  ) {}

  get totalAmount() {
    return this._pharmacyDispensableMedicine.totalAmount;
  }

  hasTotalAmount() {
    return !!this.totalAmount;
  }

  get dispensableMedicines() {
    return this._pharmacyDispensableMedicine.dispensableMedicines;
  }
}

export class DispensableMedicineModel {
  constructor(
    private _dispensableMedicine: {
      isAlternative: boolean;
      isSoldOut: boolean;
      treatmentMedicineName: string;
      medicineId: number;
      medicineName: string;
      userBasedStandard: string;
      price: number;
      unit?: number;
      prescribedMedicineId?: string | null;
    }
  ) {}
  get isAlternative() {
    return this._dispensableMedicine.isAlternative;
  }
  get medicineId() {
    return this._dispensableMedicine.medicineId;
  }

  get medicineName() {
    return this._dispensableMedicine.medicineName;
  }

  get price() {
    return this._dispensableMedicine.price ?? 0;
  }
  get unit() {
    return this._dispensableMedicine.unit;
  }

  get prescribedMedicineId() {
    return this._dispensableMedicine.prescribedMedicineId ?? '';
  }

  hasMedicinePrice() {
    return !!this.price;
  }
  hasMedicineUnit() {
    return !!this.unit;
  }
}

export class PharmacyReviewModel {
  constructor(
    private _pharmacyReview: {
      count: number;
      rating: number;
    }
  ) {}

  get count() {
    return this._pharmacyReview.count;
  }

  get rating() {
    return this._pharmacyReview.rating;
  }
}

export class PharmacyDutyModel {
  constructor(
    private _pharmacyDuty: {
      weekDay: number;
      openTime: string;
      closeTime: string;
      isClosed: boolean;
      breakStartTime: string;
      breakEndTime: string;
      hasBreakTime: boolean;
    }
  ) {}

  get weekDay() {
    return this._pharmacyDuty.weekDay;
  }

  get openTime() {
    return this._pharmacyDuty.openTime;
  }

  get closeTime() {
    return this._pharmacyDuty.closeTime;
  }

  get isClosed() {
    return this._pharmacyDuty.isClosed;
  }

  get hasBreakTime() {
    return this._pharmacyDuty.hasBreakTime;
  }

  get breakStartTime() {
    return this._pharmacyDuty.breakStartTime;
  }

  get breakEndTime() {
    return this._pharmacyDuty.breakEndTime;
  }

  shouldShowTodayBreakTime() {
    return !!this._pharmacyDuty && !this._pharmacyDuty.isClosed && this._pharmacyDuty.hasBreakTime;
  }
}

export interface MedicineSummaryVo {
  medicineId: string;
  medicineName: string;
  medicineImageUrl: string;
  hasDetailInfo: boolean;
  pharmaceuticalEffectLiteracy: string;
  medicinePid: string;
  medicineStockStatus: {
    status: MedicineDispensabilityStatus;
    imageUrl: string;
  };
}
export class MedicineInfoWithDoseDaysModel {
  constructor(
    private _prescribedMedicineInfoWithDoseDays: {
      medicineSummary: MedicineSummaryVo | null;
    }
  ) {}

  get medicineSummary() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary;
  }

  hasMedicineSummary() {
    return !!this.medicineSummary;
  }

  get medicineId() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary?.medicineId ?? '';
  }
  get medicineStockStatus() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary?.medicineStockStatus ?? undefined;
  }
  get medicineName() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary?.medicineName ?? '';
  }
  get medicineImageUrl() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary?.medicineImageUrl ?? '';
  }

  get hasDetailInfo() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary?.hasDetailInfo ?? false;
  }
  get pharmaceuticalEffectLiteracy() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary?.pharmaceuticalEffectLiteracy ?? '';
  }
  get medicinePid() {
    return this._prescribedMedicineInfoWithDoseDays.medicineSummary?.medicinePid ?? '';
  }
}
export interface PharmacyVo {
  roadAddress: string | null;
  jibunAddress: string | null;
  detailAddress: string | null;
  distance?: number | null;
  latitude: number;
  longitude: number;
  pharmacyId: string;
  pharmacyPid?: string;
  pharmacyName: string;
  pickupCompletedCount: number;
  tel: string | null;
  safetyNumber: string | null;
  faxNumber?: string;
  operationDisplay: string | null;
  operationStatus: EPharmacyOperationType;
  mainTags: PharmacyMainTagModel[];
  badges: PharmacyBadgeModel[];
  tags: PharmacyTagModel[];
  priceTags: PharmacyPriceTagModel[];
  pharmacyNextOperationHour: Array<PharmacyNextOperationHourModel>;
  pharmacyDispensableMedicinePrices?: PharmacyDispensableMedicineModel | null;
  reviewSummary?: {
    totalCount: number;
    averageRating: string;
  };
  partnershipType: PharmacyPartnershipType;
  diagnosisPrice: number | null;
  reviews?: PharmacyReviewModel[];
  todayDuty?: PharmacyDutyModel | null;
  pharmacyDuties?: PharmacyDutyModel[];
  recommendationScore?: number;
  prescribedMedicineInfoWithDoseDays?: MedicineInfoWithDoseDaysModel[];
  isFavoritePharmacy: boolean;
  parkingType: {
    name: string;
    type: EPharmacyParkingType;
  } | null;
  parkingDescription: string | null;
  directions: string | null;
}
export class PharmacyModel {
  constructor(private _pharmacy: PharmacyVo) {}

  get distance() {
    return this._pharmacy.distance;
  }

  shouldShowDistance() {
    return typeof this._pharmacy.distance === 'number' && this._pharmacy.distance > 0;
  }

  get formattedDistance() {
    if (typeof this._pharmacy.distance !== 'number') return;
    return formatKilometerByMeter(this._pharmacy.distance);
  }

  get latitude() {
    return this._pharmacy.latitude;
  }

  get longitude() {
    return this._pharmacy.longitude;
  }

  get pharmacyId() {
    return this._pharmacy.pharmacyId;
  }

  getPharmacyPid() {
    return this._pharmacy.pharmacyPid;
  }

  get pharmacyName() {
    return this._pharmacy.pharmacyName;
  }

  get pickupCompletedCount() {
    return this._pharmacy.pickupCompletedCount;
  }

  get tel() {
    return this._pharmacy.tel;
  }

  shouldShowTel() {
    return !!this.tel;
  }

  get operationDisplay() {
    return this._pharmacy.operationDisplay;
  }

  hasOperationDisplay() {
    return !!this.operationDisplay;
  }

  get operationStatus() {
    return this._pharmacy.operationStatus;
  }

  hasClosePharmacyStatus() {
    return (
      this._pharmacy.operationStatus === EPharmacyOperationType.DayOff ||
      this._pharmacy.operationStatus === EPharmacyOperationType.Closed ||
      this._pharmacy.operationStatus === EPharmacyOperationType.BeforeOpen ||
      this._pharmacy.operationStatus === EPharmacyOperationType.Unknown
    );
  }

  isAbsentPharmacyStatus() {
    return this._pharmacy.operationStatus === EPharmacyOperationType.Absent;
  }

  get isOpen() {
    return (
      this.operationStatus === EPharmacyOperationType.Open || this.operationStatus === EPharmacyOperationType.ClosedSoon
    );
  }

  get isPartnershipPharmacy() {
    return this._pharmacy.partnershipType === PharmacyPartnershipType.Partnership;
  }

  get mainTags() {
    return this._pharmacy.mainTags;
  }

  shouldShowPharmacyMainTagList() {
    return this.mainTags.length > 0;
  }

  isHandlingMedicinePharmacy() {
    return this.mainTags.some(
      (mainTag) =>
        mainTag.isHighDispensationProbability() || mainTag.isPossibleDispensation() || mainTag.isConfirmedDispensation()
    );
  }

  isHighDispensationProbabilityPharmacy() {
    return this.mainTags.some((mainTag) => mainTag.isHighDispensationProbability());
  }

  isPossibleDispensationPharmacy() {
    return this.mainTags.some((mainTag) => mainTag.isPossibleDispensation());
  }

  isConfirmDispensationPharmacy() {
    return this.mainTags.some((mainTag) => mainTag.isConfirmedDispensation());
  }

  shouldTakeImpressionLog() {
    return this.isHandlingMedicinePharmacy();
  }

  getFirstMainTagType() {
    return this.mainTags?.[0]?.type;
  }

  get badges() {
    return this._pharmacy.badges;
  }

  get tags() {
    return this._pharmacy.tags;
  }

  get faxNumber() {
    return this._pharmacy.faxNumber;
  }

  get safetyNumber() {
    return this._pharmacy.safetyNumber;
  }

  shouldShowPharmacyTagList() {
    return this.tags.length > 0;
  }

  isNowPharmacy() {
    return this.mainTags.some((mainTag) => mainTag.isConfirmedDispensation());
  }

  isImmediatePaymentPharmacy() {
    return this.tags.some((tag) => tag.isImmediatePayment());
  }
  isOpenAtLateNight() {
    return this.tags.some((tag) => tag.isOpenAtLateNight());
  }

  isOpenEveryDay() {
    return this.tags.some((tag) => tag.isOpenEveryDay());
  }

  isRequestWithAppPharmacy() {
    return this.tags.some((tag) => tag.isSendPrescriptionRequestWithApp());
  }

  isReservationWithAppPharmacy() {
    return this.tags.some((tag) => tag.isSendPrescriptionReservationWithApp());
  }

  getClusteredPharmacyModalAllowedTagList() {
    return this.tags.filter((tag) => tag.isClusteredPharmacyModalAllowedTag());
  }

  getFirstClusteredPharmacyModalAllowedTag() {
    return this.getClusteredPharmacyModalAllowedTagList().slice(0, 1);
  }

  get pharmacyNextOperationHour() {
    return this._pharmacy.pharmacyNextOperationHour;
  }

  getPrescriptionSendType() {
    if (this.isPartnershipPharmacy) {
      if (!this.isOpenPrescriptionReception()) {
        return EPrescriptionSendType.Call;
      }

      if (this.isNowPharmacy()) {
        if (this.isImmediatePaymentPharmacy()) {
          return EPrescriptionSendType.ImmediatePaymentPartnership;
        }
        if (this.isRequestWithAppPharmacy()) {
          return EPrescriptionSendType.SendPrescriptionNowPartnership;
        }
      }
      if (this.isReservationWithAppPharmacy()) {
        return EPrescriptionSendType.PrescriptionBookingPartnership;
      }

      if (this.isRequestWithAppPharmacy()) {
        return EPrescriptionSendType.SendPrescriptionPartnership;
      }

      return EPrescriptionSendType.Call;
    }

    if (this.getIsRejectedBefore()) {
      return EPrescriptionSendType.PrescriptionDeniedPharmacy;
    }

    if (this.isClosedNow()) {
      return EPrescriptionSendType.ClosedPharmacy;
    }

    return EPrescriptionSendType.Call;
  }

  get pharmacyDispensableMedicines() {
    return this._pharmacy.pharmacyDispensableMedicinePrices;
  }

  get pharmacyDispensableMedicinesTotalAmount() {
    return this._pharmacy.pharmacyDispensableMedicinePrices?.totalAmount;
  }

  get reviewSummary() {
    return this._pharmacy.reviewSummary;
  }

  get reviewSummaryAverageRating() {
    return this._pharmacy.reviewSummary?.averageRating;
  }

  get reviewSummaryTotalCount() {
    return this._pharmacy.reviewSummary?.totalCount;
  }

  isReviewSummaryTotalCountMoreThanZero() {
    if (!this._pharmacy.reviewSummary?.totalCount) {
      return false;
    }

    return this._pharmacy.reviewSummary?.totalCount > 0;
  }

  get diagnosisPrice() {
    return this._pharmacy.diagnosisPrice;
  }

  shouldShowDiagnosisPrice() {
    return !!this.diagnosisPrice;
  }

  getPharmacyDispensableMedicinePriceWithDiagnosisPrice(shouldShowDiagnosisPrice?: boolean) {
    if (shouldShowDiagnosisPrice && this._pharmacy.diagnosisPrice !== null) {
      return `${formatPriceComma(
        (this._pharmacy.pharmacyDispensableMedicinePrices?.totalAmount ?? 0) + (this._pharmacy.diagnosisPrice ?? 0)
      )}원`;
    }

    return `${formatPriceComma(this._pharmacy.pharmacyDispensableMedicinePrices?.totalAmount ?? 0)}원`;
  }

  getOriginalDataOfPharmacy() {
    return this._pharmacy;
  }

  get reviews() {
    return this._pharmacy.reviews;
  }

  get pharmacyDuties() {
    return this._pharmacy.pharmacyDuties;
  }

  get todayDuty() {
    return this._pharmacy.todayDuty;
  }

  shouldShowPharmacyDuty() {
    return !!this._pharmacy.pharmacyDuties;
  }
  get recommendationScore() {
    return this._pharmacy.recommendationScore;
  }

  get roadAddress() {
    return this._pharmacy.roadAddress;
  }

  get jibunAddress() {
    return this._pharmacy.jibunAddress;
  }

  get detailAddress() {
    return this._pharmacy.detailAddress;
  }

  get isFavoritePharmacy() {
    return this._pharmacy.isFavoritePharmacy;
  }

  updateFavoritePharmacy(isFavorite: boolean) {
    this._pharmacy = {
      ...this._pharmacy,
      isFavoritePharmacy: isFavorite,
    };
  }

  private isClosedNow() {
    return (
      this.operationStatus === EPharmacyOperationType.BeforeOpen ||
      this.operationStatus === EPharmacyOperationType.Closed ||
      this.operationStatus === EPharmacyOperationType.DayOff
    );
  }

  private isOpenPrescriptionReception() {
    return this.operationStatus !== EPharmacyOperationType.Absent;
  }

  getPharmacyFullAddress() {
    const detailAddress = this.detailAddress ? `, ${this.detailAddress}` : '';
    return `${this.roadAddress ?? this.jibunAddress}${detailAddress}`;
  }

  shouldShowPharmacyMap() {
    return !!this.latitude && !!this.longitude;
  }

  getIsRejectedBefore() {
    return this.tags.some((tag) => tag.isPrescriptionReject());
  }

  shouldShowMedicineInfoTab() {
    return !!this._pharmacy.prescribedMedicineInfoWithDoseDays?.length;
  }

  getFilteredMedicineInfos() {
    if (!this._pharmacy.prescribedMedicineInfoWithDoseDays) return [];
    return this._pharmacy.prescribedMedicineInfoWithDoseDays.filter((medicine) => medicine.hasMedicineSummary());
  }

  shouldShowMedicineInfo() {
    return !!this.getFilteredMedicineInfos();
  }

  getPrescriptionSendButtonText() {
    switch (this.getPrescriptionSendType()) {
      case EPrescriptionSendType.Call:
      case EPrescriptionSendType.ClosedPharmacy:
      case EPrescriptionSendType.PrescriptionDeniedPharmacy:
        return '전화로 접수';
      case EPrescriptionSendType.ImmediatePaymentPartnership:
      case EPrescriptionSendType.SendPrescriptionNowPartnership:
      case EPrescriptionSendType.SendPrescriptionPartnership:
      case EPrescriptionSendType.PrescriptionBookingPartnership:
        return '앱으로 접수';
    }
  }
  getFindPharmacyDiagnosisActionButtonText(
    shouldShowDiagnosisPrice: boolean,
    medicineName: string,
    isSelectedAllTab: boolean
  ) {
    if (isSelectedAllTab) return '비대면 진료로 처방 받기';
    if (this.shouldShowDiagnosisPrice()) {
      return `${this.getPharmacyDispensableMedicinePriceWithDiagnosisPrice(
        shouldShowDiagnosisPrice
      )}에 ${medicineName} 처방`;
    }
    if (isWebView()) {
      return `${medicineName} 처방받으러 가기`;
    }
    return `${medicineName} 진료 할인받기`;
  }

  get priceTags() {
    return this._pharmacy.priceTags;
  }
  shouldShowPriceTags() {
    return !!this.priceTags.length;
  }
  hasMedicinePrice() {
    return (
      !!this._pharmacy.pharmacyDispensableMedicinePrices &&
      this._pharmacy.pharmacyDispensableMedicinePrices.hasTotalAmount()
    );
  }

  get parkingType() {
    return this._pharmacy.parkingType;
  }

  get parkingDescription() {
    return this._pharmacy.parkingDescription;
  }

  get directions() {
    return this._pharmacy.directions;
  }

  shouldShowDirections() {
    return !!this.directions;
  }

  shouldShowParkingInfo() {
    return !!this.parkingType || !!this.parkingDescription;
  }

  getParkingTypeDisplayName() {
    return this.parkingType?.name || '';
  }

  shouldShowLocationInfo() {
    return this.shouldShowDirections() || this.shouldShowParkingInfo();
  }
}

export class PharmacyPriceTagModel {
  readonly type;
  readonly imageUrl;

  constructor(pharmacyPriceBadge: { type: EPharmacyPriceTagType; imageUrl: string }) {
    this.type = pharmacyPriceBadge.type;
    this.imageUrl = pharmacyPriceBadge.imageUrl;
  }

  isVerifiedPrice() {
    return this.type === EPharmacyPriceTagType.verifiedPrice;
  }
}

export class PharmacyAttributeModel {
  readonly mainTags;
  readonly badges;
  readonly tags;
  readonly priceTags;

  constructor(pharmacyPriceBadge: {
    mainTags: PharmacyMainTagModel[];
    badges: PharmacyBadgeModel[];
    tags: PharmacyTagModel[];
    priceTags: PharmacyPriceTagModel[];
  }) {
    this.mainTags = pharmacyPriceBadge.mainTags;
    this.badges = pharmacyPriceBadge.badges;
    this.tags = pharmacyPriceBadge.tags;
    this.priceTags = pharmacyPriceBadge.priceTags;
  }

  isImmediatePaymentPharmacy() {
    return this.tags.some((tag) => tag.isImmediatePayment());
  }

  isConfirmedDispensationPharmacy() {
    return this.mainTags.some((mainTag) => mainTag.isConfirmedDispensation());
  }
}

export class MedicineFilterModel {
  readonly name: string;
  readonly description?: string;
  readonly unitDisplayName: string;

  constructor(
    readonly treatmentMedicine: NearbyPharmacyMedicineVo,
    readonly dispensationUnit: DispensationUnitVo,
    readonly treatmentMedicineId: string,
    readonly treatmentMedicineStrengthId?: string
  ) {
    this.name = treatmentMedicine?.name;
    this.description = treatmentMedicine?.description;
    this.unitDisplayName = dispensationUnit?.dispensationUnitDisplayName;
    this.treatmentMedicineId = treatmentMedicineId;
    this.treatmentMedicineStrengthId = treatmentMedicineStrengthId;
  }

  medicineDisplayName() {
    return `${this.name} ${this.description}`;
  }
}
